import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

/**
 * Per-user tip preferences, shared by the Home tips card and the in-context tips.
 * Kept in this browser only; missing/blocked storage just means tips may show again.
 *
 * Rules that keep tips from becoming a nuisance:
 *  - a dismissed or acted-on tip never shows again (`seen`)
 *  - an ignored tip retires by itself after showing on MAX_TIP_DAYS different days (`views`)
 *  - `off` (the ✕ on the Home card) turns every tip off
 *  - the Home card has its own pacing fields (see FeatureTipCard)
 */
export type TipPrefs = {
  seen: string[];
  off: boolean;
  /** tip id → local dates (YYYY-MM-DD) it was shown on */
  views: Record<string, string[]>;
  /** Home card: tip currently shown, the day a new tip was last introduced, ignored-in-a-row count, self-disabled */
  cardTip: string | null;
  cardDay: string;
  cardIgnored: number;
  cardOff: boolean;
};

export const MAX_TIP_DAYS = 3;

const prefsKey = (userId: string) => `feature-tips:${userId}`;
// Many components use this hook at once (e.g. one medicine search per row); keep them in sync
const CHANGED_EVENT = 'tip-prefs-changed';

export const today = () => new Date().toLocaleDateString('en-CA'); // local YYYY-MM-DD

function loadPrefs(userId: string): TipPrefs {
  let p: any = {};
  try { p = JSON.parse(localStorage.getItem(prefsKey(userId)) || '{}'); } catch { /* use defaults */ }
  return {
    seen: Array.isArray(p.seen) ? p.seen : [],
    off: !!p.off,
    views: p.views && typeof p.views === 'object' ? p.views : {},
    cardTip: typeof p.cardTip === 'string' ? p.cardTip : null,
    cardDay: typeof p.cardDay === 'string' ? p.cardDay : '',
    cardIgnored: Number(p.cardIgnored) || 0,
    cardOff: !!p.cardOff,
  };
}

const OFF: TipPrefs = { seen: [], off: true, views: {}, cardTip: null, cardDay: '', cardIgnored: 0, cardOff: true };

export function useTipPrefs() {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const [prefs, setPrefs] = useState<TipPrefs>(() => userId ? loadPrefs(userId) : OFF);

  useEffect(() => {
    if (!userId) return;
    setPrefs(loadPrefs(userId));
    const reload = () => setPrefs(loadPrefs(userId));
    window.addEventListener(CHANGED_EVENT, reload);
    return () => window.removeEventListener(CHANGED_EVENT, reload);
  }, [userId]);

  /** Apply a change. Return `p` itself for "no change" — then nothing is written or re-rendered. */
  const update = useCallback((fn: (p: TipPrefs) => TipPrefs) => {
    if (!userId) return;
    // Re-read so changes made elsewhere in the app since this mounted aren't lost
    const current = loadPrefs(userId);
    const next = fn(current);
    if (next === current) return;
    try { localStorage.setItem(prefsKey(userId), JSON.stringify(next)); } catch { /* storage unavailable */ }
    window.dispatchEvent(new Event(CHANGED_EVENT));
  }, [userId]);

  const markSeen = useCallback((id: string) => update(p => p.seen.includes(id) ? p : { ...p, seen: [...p.seen, id] }), [update]);
  const turnOff = useCallback(() => update(p => p.off ? p : { ...p, off: true }), [update]);
  /** Call when a tip is actually on screen; counts toward MAX_TIP_DAYS */
  const recordView = useCallback((id: string) => update(p => {
    const days = p.views[id] || [];
    return days.includes(today()) ? p : { ...p, views: { ...p.views, [id]: [...days, today()] } };
  }), [update]);

  const isActive = (id: string) => {
    if (!userId || prefs.off || prefs.seen.includes(id)) return false;
    const days = prefs.views[id] || [];
    return days.includes(today()) || days.length < MAX_TIP_DAYS;
  };

  return { prefs, update, markSeen, turnOff, recordView, isActive };
}
